import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { deleteFlight, getFlights, getUsers, splitFlight, updateFlight, addIssue, updateIssue, uploadFlightFiles } from '../../api';
import { EditFlightPayload, Flight } from '../../types';
import { DateFormat } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import { flightPilotOptions, canEditFlight, isGeneralUser } from '../../utils/isGeneralUser';
import {
  SortConfig,
  defaultDirectionForKind,
  sortByConfig,
  toggleSortConfig,
} from '../../utils/tableSort';
import {
  FLIGHT_SORT_SECONDARY,
  FlightSortKey,
  flightSortKind,
  flightSortValue,
} from '../../utils/flightSort';
import { useAuth } from '../../hooks/useAuth';
import { useAircraft } from '../../aircraft/AircraftContext';
import { useFeedback } from '../../hooks/useFeedback';
import { useLoadMore } from '../../hooks/useLoadMore';
import EditFlightModal from '../../components/EditFlightModal/EditFlightModal';
import SplitFlightModal from '../../components/SplitFlightModal/SplitFlightModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import FlightDetailsModal from '../../components/FlightDetailsModal/FlightDetailsModal';
import LoadMoreButton from '../../components/LoadMoreButton/LoadMoreButton';
import PilotBadge from '../../components/PilotBadge/PilotBadge';
import SortableTh from '../../components/SortableTh/SortableTh';
import { downloadFlightsCsv } from '../../utils/exportFlightsCsv';
import { flightDurationMh } from '../../utils/flightDisplay';
import { uploadIssueFiles } from '../../utils/issueFiles';
import '../FlightsHistoryPage/FlightsHistoryPage.css';

const FlightsHistorySection = () => {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [users, setUsers] = useState<
    { id: number; name: string; login?: string; role?: string }[]
  >([]);
  const [viewingFlight, setViewingFlight] = useState<Flight | null>(null);
  const [editingFlight, setEditingFlight] = useState<(Flight & { pilot_id: number }) | null>(
    null
  );
  const [splittingFlight, setSplittingFlight] = useState<Flight | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { isAdmin, currentUserId, canFlyOrBook } = useAuth();
  const { aircraftId, current, dataRevision } = useAircraft();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightFlightId = Number(searchParams.get('flight_id')) || null;

  const [sortConfig, setSortConfig] = useState<SortConfig<FlightSortKey>>({
    key: 'date',
    direction: 'desc',
  });

  const editPilotOptions = useMemo(
    () =>
      flightPilotOptions(users, {
        isAdmin,
        currentUserId,
      }),
    [users, isAdmin, currentUserId]
  );

  const splitPilotOptions = useMemo(
    () => flightPilotOptions(users, { isAdmin: true }),
    [users]
  );

  const loadData = useCallback(async () => {
    try {
      const [flightsRes, usersRes] = await Promise.all([getFlights(), getUsers()]);
      setFlights(flightsRes.data);
      setUsers(usersRes.data);
      setViewingFlight((current) =>
        current
          ? flightsRes.data.find((item) => item.id === current.id) ?? current
          : current
      );
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка загрузки полётов'));
    }
  }, [showError]);

  useEffect(() => {
    void loadData();
  }, [aircraftId, dataRevision, loadData]);

  useEffect(() => {
    if (!highlightFlightId || flights.length === 0) return;
    const found = flights.find((item) => item.id === highlightFlightId);
    if (found) setViewingFlight(found);
  }, [highlightFlightId, flights]);

  const clearHighlight = () => {
    if (!highlightFlightId) return;
    searchParams.delete('flight_id');
    setSearchParams(searchParams, { replace: true });
  };

  const closeViewing = () => {
    setViewingFlight(null);
    clearHighlight();
  };

  const handleDeleteConfirm = async () => {
    if (deleteId == null) return;
    setDeleting(true);
    try {
      await deleteFlight(deleteId);
      setDeleteId(null);
      setViewingFlight(null);
      await loadData();
      showSuccess('Полёт удалён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка удаления'));
    }
    setDeleting(false);
  };

  const handleEdit = (flight: Flight) => {
    const pilot = users.find((u) => u.name === flight.pilot);
    setViewingFlight(null);
    setEditingFlight({
      ...flight,
      pilot_id: pilot?.id || flight.pilot_id,
    });
  };

  const handleSaveEdit = async (
    data: EditFlightPayload,
    extras: {
      tachFiles: File[];
      issueId: number | null;
      issueDescription: string;
      issueSeverity: string;
      issueFiles: File[];
    }
  ) => {
    if (!editingFlight) return;
    try {
      await updateFlight(editingFlight.id, data);
      if (extras.tachFiles.length > 0) {
        await uploadFlightFiles(editingFlight.id, extras.tachFiles);
      }
      if (extras.issueDescription) {
        if (extras.issueId) {
          await updateIssue(extras.issueId, {
            description: extras.issueDescription,
            severity: extras.issueSeverity,
            issue_date: data.flight_date,
          });
          if (extras.issueFiles.length > 0) {
            await uploadIssueFiles(extras.issueId, extras.issueFiles);
          }
        } else {
          const selectedPilot = users.find((u) => u.id === data.pilot_id);
          const issueAuthorId =
            selectedPilot && isGeneralUser(selectedPilot)
              ? currentUserId
              : data.pilot_id;
          const created = await addIssue({
            user_id: issueAuthorId,
            description: extras.issueDescription,
            severity: extras.issueSeverity,
            flight_id: editingFlight.id,
            issue_date: data.flight_date,
          });
          if (extras.issueFiles.length > 0 && created.data.id) {
            await uploadIssueFiles(created.data.id, extras.issueFiles);
          }
        }
      }
      setEditingFlight(null);
      const res = await getFlights();
      setFlights(res.data);
      showSuccess('Полёт обновлён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка обновления'));
    }
  };

  const handleSplitConfirm = async (shares: { pilot_id: number; hours: number }[]) => {
    if (!splittingFlight) return;
    try {
      await splitFlight(splittingFlight.id, shares);
      setSplittingFlight(null);
      await loadData();
      showSuccess('Полёт разделён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка разделения'));
    }
  };

  const handleSort = (key: FlightSortKey) => {
    const kind = flightSortKind(key);
    const defaultDir =
      key === 'motohours_end' || key === 'id' ? 'desc' : defaultDirectionForKind(kind);
    setSortConfig((prev) => toggleSortConfig(prev, key, defaultDir));
  };

  const sortedFlights = useMemo(
    () =>
      sortByConfig(
        flights,
        sortConfig,
        flightSortValue,
        flightSortKind,
        FLIGHT_SORT_SECONDARY.filter((s) => s.key !== sortConfig.key)
      ),
    [flights, sortConfig]
  );

  const highlightIndex = useMemo(() => {
    if (!highlightFlightId) return null;
    const idx = sortedFlights.findIndex((f) => f.id === highlightFlightId);
    return idx >= 0 ? idx : null;
  }, [sortedFlights, highlightFlightId]);

  const { visibleCount, hasMore, remaining, nextBatch, loadMore, loadAll } = useLoadMore(
    sortedFlights.length,
    {
      resetKey: `${sortConfig.key}-${sortConfig.direction}`,
      ensureIndex: highlightIndex,
    }
  );
  const visibleFlights = sortedFlights.slice(0, visibleCount);

  const handleExport = () => {
    if (sortedFlights.length === 0) {
      showError('Нет полётов для выгрузки');
      return;
    }
    const tail = current?.tail_number?.trim() || `aircraft-${aircraftId}`;
    downloadFlightsCsv(sortedFlights, `flights-${tail}`);
    showSuccess('CSV скачан');
  };

  return (
    <section id="history" className="stats-history-card">
      {feedbackNode}
      <div className="stats-history-head">
        <h3>История полётов</h3>
        <button
          type="button"
          className="btn-export-flights"
          onClick={handleExport}
          disabled={sortedFlights.length === 0}
        >
          Скачать CSV
        </button>
      </div>

      <div className="flights-history-table-wrap">
        <table className="flights-history-table flights-history-table-compact">
          <thead>
            <tr>
              <SortableTh
                label="Дата"
                columnKey="date"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <SortableTh
                label="Пилот"
                columnKey="pilot"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
              <SortableTh
                label="Моточасы"
                columnKey="hours"
                sortConfig={sortConfig}
                onSort={handleSort}
              />
            </tr>
          </thead>
          <tbody>
            {visibleFlights.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  style={{ textAlign: 'center', padding: '30px', color: '#7f8c8d' }}
                >
                  Нет полётов
                </td>
              </tr>
            ) : (
              visibleFlights.map((f) => {
                const isHighlighted = highlightFlightId === f.id;
                return (
                  <tr
                    key={f.id}
                    className={`flights-history-row${
                      isHighlighted ? ' row-highlight' : ''
                    }`}
                    onClick={() => setViewingFlight(f)}
                  >
                    <td>{DateFormat(f.date)}</td>
                    <td>
                      <PilotBadge name={f.pilot} color={f.pilot_color} />
                    </td>
                    <td>{flightDurationMh(f)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {hasMore ? (
        <LoadMoreButton
          remaining={remaining}
          nextBatch={nextBatch}
          onClick={loadMore}
          onShowAll={loadAll}
        />
      ) : null}

      {viewingFlight ? (
        <FlightDetailsModal
          flight={viewingFlight}
          canEdit={canEditFlight(viewingFlight.pilot_id, {
            isAdmin,
            canFlyOrBook,
            currentUserId,
          })}
          canAdmin={isAdmin}
          onClose={closeViewing}
          onEdit={() => handleEdit(viewingFlight)}
          onSplit={() => {
            setSplittingFlight(viewingFlight);
            setViewingFlight(null);
          }}
          onDelete={() => {
            setDeleteId(viewingFlight.id);
            setViewingFlight(null);
          }}
          onOpenIssue={
            viewingFlight.issue
              ? () => navigate(`/issues?flight_id=${viewingFlight.id}`)
              : undefined
          }
        />
      ) : null}

      {editingFlight && (
        <EditFlightModal
          flight={editingFlight}
          pilots={editPilotOptions}
          defaultFuelLocation={current?.base_airfield?.trim() || ''}
          allowFullSplit={isAdmin}
          onClose={() => setEditingFlight(null)}
          onSave={handleSaveEdit}
        />
      )}

      {splittingFlight && (
        <SplitFlightModal
          flightId={splittingFlight.id}
          flightHours={splittingFlight.hours}
          pilots={splitPilotOptions}
          onClose={() => setSplittingFlight(null)}
          onSplit={handleSplitConfirm}
        />
      )}

      {deleteId != null && (
        <ConfirmModal
          title="Удалить полёт?"
          message="Полёт будет удалён без возможности восстановления."
          confirmLabel="Удалить"
          danger
          submitting={deleting}
          onCancel={() => setDeleteId(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </section>
  );
};

export default FlightsHistorySection;
